import { ArrowLeft, ArrowRight, ExternalLink, Link as LinkIcon, Plus, Save, Upload, X } from 'lucide-react';
import React, { useState } from 'react';
import { useChatbot } from '../../contexts/ChatbotContext';
import { useDesignColors } from '../../hooks/useDesignColors';
import type { Product } from '../../hooks/useProductSystem';
import { useProductSystem } from '../../hooks/useProductSystem';
import { supabase } from '../../lib/supabase';

interface ProductFormData {
  name: string;
  description: string;
  price: string;
  discount: string;
  currency: string;
  images: Array<{
    url: string;
    type: 'upload' | 'external';
  }>;
  category: string;
  tags: string[];
  sku: string;
  stock: string;
  redirect_url?: string;
}

const initialFormData: ProductFormData = {
  name: '',
  description: '',
  price: '',
  discount: '',
  currency: 'EUR',
  images: [],
  category: '',
  tags: [],
  sku: '',
  stock: '',
  redirect_url: ''
};

type Step = 'basic' | 'images' | 'pricing' | 'inventory' | 'categorization';

interface ProductFormProps {
  product?: Product;
  onClose: () => void;
}

export function ProductForm({ product, onClose }: ProductFormProps) {
  const { chatbotPublicId } = useChatbot();
  const { addProduct, updateProduct, categories, tags, loading } = useProductSystem();
  const { colors } = useDesignColors();
  const [formData, setFormData] = useState<ProductFormData>(() => {
    if (product) {
      return {
        name: product.name,
        description: product.description || '',
        price: product.price?.toString() || '',
        discount: product.discount?.toString() || '',
        currency: product.currency || 'EUR',
        images: [
          ...(product.image_url ? [{ url: product.image_url, type: 'upload' as const }] : []),
          ...(product.metadata?.additional_images?.map(url => ({ 
            url, 
            type: 'upload' as const 
          })) || [])
        ],
        category: product.category || '',
        tags: product.tags || [],
        sku: product.sku || '',
        stock: product.stock?.toString() || '',
        redirect_url: product.metadata?.redirect_url
      };
    }
    return initialFormData;
  });
  const [currentStep, setCurrentStep] = useState<Step>('basic');
  const [imageUploading, setImageUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [showImageUrlInput, setShowImageUrlInput] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const steps: { id: Step; label: string }[] = [
    { id: 'basic', label: 'Basic informations' },
    { id: 'images', label: 'Images' },
    { id: 'pricing', label: 'Pricing' },
    { id: 'inventory', label: 'Stock' },
    { id: 'categorization', label: 'Categories & Tags' }
  ];

  const handleFinalSubmit = async () => {
    if (!chatbotPublicId || !formData.name.trim()) {
      setFormError('Please fill in all required fields');
      return;
    }

    try {
      const productData = {
        name: formData.name,
        description: formData.description,
        price: parseFloat(formData.price),
        discount: formData.discount ? parseFloat(formData.discount) : undefined,
        currency: formData.currency,
        image_url: formData.images[0]?.url,
        category: formData.category,
        tags: formData.tags,
        sku: formData.sku,
        stock: parseInt(formData.stock),
        source: 'manual' as const,
        metadata: {
          additional_images: formData.images.slice(1).map(img => img.url),
          redirect_url: formData.redirect_url
        }
      };

      if (product) {
        await updateProduct(chatbotPublicId, product.id, productData);
      } else {
        await addProduct(chatbotPublicId, productData);
      }
      onClose();
    } catch (err) {
      console.error('Error saving product:', err);
      setFormError(err instanceof Error ? err.message : 'Error saving product');
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isLastStep) {
      handleFinalSubmit();
    } else {
      goToNextStep();
    }
  };

  const handleImageUpload = async (file: File) => {
    try {
      setImageUploading(true);
      setUploadError(null);

      if (!file || !chatbotPublicId) {
        return;
      }

      // Validation du type de fichier
      if (!file.type.startsWith('image/')) {
        setUploadError('The file must be an image (JPG, PNG, GIF)');
        return;
      }

      // Validation de la taille (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        setUploadError('The image must not exceed 10MB');
        return;
      }

      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}.${fileExt}`;
      const filePath = `products/${chatbotPublicId}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('chatbot-assets')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('chatbot-assets')
        .getPublicUrl(filePath);

      setFormData(prev => ({
        ...prev,
        images: [...prev.images, { url: publicUrl, type: 'upload' }]
      }));
    } catch (err) {
      console.error('Error uploading image:', err);
      setUploadError('Error uploading image');
    } finally {
      setImageUploading(false);
    }
  };

  const handleAddImageUrl = () => {
    if (!imageUrl.trim()) return;

    setFormData(prev => ({
      ...prev,
      images: [...prev.images, { url: imageUrl.trim(), type: 'external' }]
    }));
    setImageUrl('');
    setShowImageUrlInput(false);
  };

  const handleRemoveImage = (index: number) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const validateCurrentStep = (): boolean => {
    setFormError(null);
    switch (currentStep) {
      case 'basic':
        if (!formData.name.trim()) {
          setFormError('Product name is required');
          return false;
        }
        break;
      case 'pricing':
        if (formData.price && isNaN(parseFloat(formData.price))) {
          setFormError('Price must be a valid number');
          return false;
        }
        if (formData.discount && (isNaN(parseFloat(formData.discount)) || parseFloat(formData.discount) > 100)) {
          setFormError('The discount must be a valid percentage (0-100)');
          return false;
        }
        break;
      case 'inventory':
        if (formData.stock && isNaN(parseInt(formData.stock))) {
          setFormError('The stock must be an integer');
          return false;
        }
        break;
    }
    return true;
  };

  const currentStepIndex = steps.findIndex(step => step.id === currentStep);
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === steps.length - 1;

  const goToNextStep = () => {
    if (!isLastStep && validateCurrentStep()) {
      setCurrentStep(steps[currentStepIndex + 1].id);
    }
  };

  const goToPreviousStep = () => {
    if (!isFirstStep) {
      setCurrentStep(steps[currentStepIndex - 1].id);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 'basic':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Product name <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                rows={3}
              />
            </div>
          </div>
        );

      case 'images':
        return (
          <div className="space-y-4">
            {/* Liste des images */}
            <div className="grid grid-cols-4 gap-4 mb-4">
              {formData.images.map((image, index) => (
                <div key={index} className="relative aspect-square group">
                  <img
                    src={image.url}
                    alt={`Product ${index + 1}`}
                    className="w-full h-full object-cover rounded-lg"
                  />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleRemoveImage(index)}
                      className="p-1 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}

              {/* Zone d'upload */}
              <div className="aspect-square">
                <label className="w-full h-full flex flex-col items-center justify-center border-2 border-dashed border-white/10 rounded-lg hover:border-indigo-500/50 transition-colors cursor-pointer">
                  <input
                    type="file"
                    className="hidden"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleImageUpload(file);
                    }}
                    disabled={imageUploading}
                  />
                  <Upload className="w-6 h-6 text-gray-400 mb-2" />
                  <span className="text-xs text-gray-400">
                    Add image
                  </span>
                </label>
              </div>
            </div>

            {/* Input URL */}
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => setShowImageUrlInput(!showImageUrlInput)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 text-gray-300 hover:bg-white/10 transition-colors text-sm"
              >
                <LinkIcon className="w-4 h-4" />
                Add from URL
              </button>

              {showImageUrlInput && (
                <div className="flex gap-2">
                  <input
                    type="url"
                    value={imageUrl}
                    onChange={(e) => setImageUrl(e.target.value)}
                    placeholder="https://..."
                    className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <button
                    type="button"
                    onClick={handleAddImageUrl}
                    className="px-4 py-2 rounded-lg bg-indigo-500/20 text-indigo-400 hover:bg-indigo-500/30 transition-colors"
                  >
                    Add
                  </button>
                </div>
              )}
            </div>

            {uploadError && (
              <p className="text-sm text-red-400 mt-2">
                {uploadError}
              </p>
            )}
          </div>
        );

      case 'pricing':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Price
              </label>
              <div className="flex gap-2">
                <input
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                  className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <select
                  value={formData.currency}
                  onChange={(e) => setFormData(prev => ({ ...prev, currency: e.target.value }))}
                  className="w-20 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="EUR">€</option>
                  <option value="USD">$</option>
                  <option value="GBP">£</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Discount (%)
              </label>
              <input
                type="number"
                min="0"
                max="100"
                step="1"
                value={formData.discount}
                onChange={(e) => setFormData(prev => ({ ...prev, discount: e.target.value }))}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Ex: 20"
              />
            </div>
          </div>
        );

      case 'inventory':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                SKU
              </label>
              <input
                type="text"
                value={formData.sku}
                onChange={(e) => setFormData(prev => ({ ...prev, sku: e.target.value }))}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Stock
              </label>
              <input
                type="number"
                value={formData.stock}
                onChange={(e) => setFormData(prev => ({ ...prev, stock: e.target.value }))}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>
        );

      case 'categorization':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Category
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">Select a category</option>
                {categories.map(category => (
                  <option key={category.id} value={category.name}>{category.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Tags
              </label>
              <select
                multiple
                value={formData.tags}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  tags: Array.from(e.target.selectedOptions, option => option.value)
                }))}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                {tags.map(tag => (
                  <option key={tag.id} value={tag.name}>{tag.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Redirect URL
              </label>
              <div className="flex gap-2">
                <input
                  type="url"
                  value={formData.redirect_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, redirect_url: e.target.value }))}
                  placeholder="https://..."
                  className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                {formData.redirect_url && (
                  <a
                    href={formData.redirect_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-2 rounded-lg bg-white/5 text-gray-300 hover:bg-white/10 transition-colors"
                  >
                    <ExternalLink className="w-5 h-5" />
                  </a>
                )}
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
      <div className="bg-gray-900 rounded-xl w-full max-w-2xl">
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <h3 className="text-lg font-semibold text-white">
            {product ? 'Edit product' : 'Add product'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Progress bar */}
        <div className="px-6 pt-4">
          <div className="flex items-center justify-between mb-2">
            {steps.map((step, index) => (
              <React.Fragment key={step.id}>
                <div className="flex flex-col items-center">
                  <div 
                    className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      index <= currentStepIndex
                        ? 'bg-indigo-500 text-white'
                        : 'bg-white/5 text-gray-400'
                    }`}
                  >
                    {index + 1}
                  </div>
                  <span className="text-xs text-gray-400 mt-1">{step.label}</span>
                </div>
                {index < steps.length - 1 && (
                  <div 
                    className={`flex-1 h-0.5 mx-2 ${
                      index < currentStepIndex
                        ? 'bg-indigo-500'
                        : 'bg-white/5'
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {formError && (
            <div className="mb-4 bg-red-500/10 text-red-400 p-3 rounded-lg text-sm">
              {formError}
            </div>
          )}

          {renderStepContent()}

          <div className="flex justify-between mt-6">
            <button
              type="button"
              onClick={goToPreviousStep}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                isFirstStep
                  ? 'opacity-0 cursor-default'
                  : 'bg-white/5 text-gray-300 hover:bg-white/10'
              }`}
              disabled={isFirstStep}
            >
              <ArrowLeft className="w-4 h-4" />
              Previous
            </button>

            {isLastStep ? (
              <button
                type="submit"
                disabled={loading || !formData.name.trim()}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-500/20 text-indigo-400 hover:bg-indigo-500/30 transition-colors disabled:opacity-50"
              >
                {product ? (
                  <>
                    <Save className="w-4 h-4" />
                    Save
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    Create the product
                  </>
                )}
              </button>
            ) : (
              <button
                type="submit"
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-500/20 text-indigo-400 hover:bg-indigo-500/30 transition-colors"
              >
                Next
                <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}